import { messages } from "@/messages";
import {
  CampaignType,
  EditMode,
  Field,
  FieldConfiguration,
  Fields,
  FieldsInputTypes,
} from "./template.interface";
import * as yup from "yup";
import { ALPHANUMERIC_WITH_HYPHEN_UNDERSCORE_SPACE_REGEX } from "@/constraints";
import { calculateVisualLength, getVisualLimit } from "@/utils/textValidator";
export function fieldsValidationSchema(
  fieldsByVariant: Record<string | number, Field>
) {
  const dynamicFieldsSchema = dynamicFieldsValidationSchema(fieldsByVariant);
  const validationSchema = yup.object({
    advertiserId: yup.number().nullable().required(messages.requiredField),
    creativeName: yup
      .string()
      .matches(
        ALPHANUMERIC_WITH_HYPHEN_UNDERSCORE_SPACE_REGEX,
        messages.alphaNumericOrHyphenOrUnderscore
      )
      .max(255)
      .required(messages.requiredField),
    ...dynamicFieldsSchema,
  });

  return validationSchema;
}

export function dynamicFieldsValidationSchema(
  fieldsByVariant: Record<string | number, Field>,
  selectFieldsInputTypeArray: FieldsInputTypes[] = []
) {
  const dynamicFieldsSchema: any = {};
  for (const variantFields in fieldsByVariant) {
    const allVariantFields = Object.values(fieldsByVariant[variantFields]);
    const fieldsToValidate = selectFieldsInputTypeArray.length
      ? allVariantFields.filter((field) =>
          selectFieldsInputTypeArray.includes(field.inputType)
        )
      : allVariantFields;
    dynamicFieldsSchema[variantFields] = yup.object(
      getValidationSchema(fieldsToValidate)
    );
  }
  return dynamicFieldsSchema;
}

export function aiFormFieldValidationSchema(campaignType: CampaignType) {
  return yup.object({
    advertiserId: yup.number().required(messages.requiredField).nullable(),
    campaignType: yup.string().required(messages.requiredField),
    campaignObjective: yup.string().nullable().required(messages.requiredField),
    productServiceDescription: yup.string().required(messages.requiredField),
    valueProposition: yup.string(),
    toneStyle: yup.string().nullable(),
    keywords: yup.array().of(yup.string()),
    cta_text: yup.string().nullable(),
    ...(campaignType === "personalized"
      ? {
          audienceId: yup.string().nullable().required(messages.requiredField),
          personalizationVariables: yup.array().of(yup.string()),
        }
      : {}),
  });
}

export function fallbackPersonalizedFieldValidationSchema(
  fieldsByVariant: Record<string | number, Field>
) {
  const dynamicFieldsSchema = dynamicFieldsValidationSchema(fieldsByVariant, [
    "text",
  ]);

  const validationSchema = yup.object({
    ...dynamicFieldsSchema,
  });

  return validationSchema;
}
function getValidationSchema(fields: FieldConfiguration[]) {
  const validationSchema: Record<string, any> = {};
  for (const field of fields) {
    validationSchema[field.name] = fieldValidation(
      field.inputType,
      field?.validations
    );
  }
  return validationSchema;
}

function fieldValidation(
  fieldType: FieldsInputTypes,
  validations?: Record<any, Record<string, any> | undefined>
) {
  const validation = validations?.[fieldType];
  if (fieldType === "color") {
    return yup.object().required(messages.requiredField);
  }

  if (fieldType === "text") {
    return (
      yup
        .string()
        .max(validation?.maxLength ?? Infinity, messages.exceedsMaxLengthLimit)
        .min(validation?.minLength ?? 0)
        // .test(
        //   "visual-length",
        //   "Text is too wide and may not fit properly",
        //   function (value) {
        //     if (!value) return true; // Allow empty if not required

        //     const maxLength = validation?.maxLength;
        //     if (!maxLength) return true; // No limit set

        //     const visualLength = calculateVisualLength(value);
        //     const visualLimit = getVisualLimit(maxLength); // Your threshold

        //     return visualLength <= visualLimit;
        //   }
        // ) // Enable this after updating max length values on template side
        .required(messages.requiredField)
    );
  }

  if (fieldType === "image") {
    return yup.mixed().required(messages.requiredField);
  }

  if (fieldType === "url") {
    return yup
      .string()
      .test(
        "url",
        messages.invalidUrl,
        (value: string | undefined) =>
          !value || new RegExp(/^https?:\/\//i).test(value)
      )
      .required(messages.requiredField);
  }

  return yup.string().required();
}
